import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AlertTriangle, Check, Loader, Search, X } from 'lucide-react';
import {
  EnquiryColumnDef,
  EnquiryColumnGroup,
  EnquiryImportConfig,
} from '../utils/enquiryColumns';
import '../column-config.css';

interface Props {
  show: boolean;
  columns: EnquiryColumnDef[];
  groups: EnquiryColumnGroup[];
  config: EnquiryImportConfig;
  onClose: () => void;
  onSave: (enabledColumns: string[], mandatoryColumns: string[]) => Promise<void>;
}

/**
 * Tenant-level picker for the enquiry import sheet: which of the columns the
 * Java ingestor supports this tenant actually uses, and which of those are
 * mandatory. Saved to customer_detail_master.JsonConfig.enquiryImport.
 */
const ColumnConfigModal: React.FC<Props> = ({ show, columns, groups, config, onClose, onSave }) => {
  const [enabled, setEnabled] = useState<Set<string>>(new Set(config.enabledColumns));
  const [mandatory, setMandatory] = useState<Set<string>>(new Set(config.mandatoryColumns));
  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed on every open so a cancelled edit is discarded.
  useEffect(() => {
    if (!show) return;
    setEnabled(new Set(config.enabledColumns));
    setMandatory(new Set(config.mandatoryColumns));
    setError(null);
  }, [show, config]);

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups
      .map(group => ({
        group,
        columns: columns.filter(c => {
          if (c.group !== group.key) return false;
          if (onlySelected && !enabled.has(c.key)) return false;
          if (!term) return true;
          return (
            c.label.toLowerCase().includes(term) ||
            c.key.includes(term) ||
            (c.aliases ?? []).some(a => a.toLowerCase().includes(term))
          );
        }),
      }))
      .filter(g => g.columns.length > 0);
  }, [columns, groups, search, onlySelected, enabled]);

  const toggleEnabled = (key: string) => {
    const nextEnabled = new Set(enabled);
    if (nextEnabled.has(key)) {
      nextEnabled.delete(key);
      // A column that is not in the sheet cannot be mandatory.
      if (mandatory.has(key)) {
        const nextMandatory = new Set(mandatory);
        nextMandatory.delete(key);
        setMandatory(nextMandatory);
      }
    } else {
      nextEnabled.add(key);
    }
    setEnabled(nextEnabled);
  };

  const toggleMandatory = (key: string) => {
    const nextMandatory = new Set(mandatory);
    if (nextMandatory.has(key)) {
      nextMandatory.delete(key);
    } else {
      nextMandatory.add(key);
      // Marking a column mandatory implies including it in the sheet.
      if (!enabled.has(key)) setEnabled(new Set(enabled).add(key));
    }
    setMandatory(nextMandatory);
  };

  const setGroupEnabled = (groupKey: string, value: boolean) => {
    const keys = columns.filter(c => c.group === groupKey).map(c => c.key);
    setEnabled(prev => {
      const next = new Set(prev);
      keys.forEach(k => (value ? next.add(k) : next.delete(k)));
      return next;
    });
    if (!value) {
      setMandatory(prev => {
        const next = new Set(prev);
        keys.forEach(k => next.delete(k));
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (enabled.size === 0) {
      setError('Select at least one column.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(Array.from(enabled), Array.from(mandatory));
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? () => undefined : onClose} size="xl" centered scrollable>
      <Modal.Header>
        <Modal.Title>Enquiry Import Columns</Modal.Title>
        <button className="ec-close" onClick={onClose} disabled={saving} aria-label="Close">
          <X size={18} />
        </button>
      </Modal.Header>

      <Modal.Body className="ec-body">
        <div className="ec-toolbar">
          <div className="ec-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search columns…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <label className="ec-filter">
            <input
              type="checkbox"
              checked={onlySelected}
              onChange={e => setOnlySelected(e.target.checked)}
            />
            Selected only
          </label>
          <div className="ec-counts">
            <span className="ec-count ec-count-enabled">{enabled.size} selected</span>
            <span className="ec-count ec-count-mandatory">{mandatory.size} mandatory</span>
          </div>
        </div>

        {visibleGroups.length === 0 && (
          <p className="ec-empty">No columns match “{search}”.</p>
        )}

        {visibleGroups.map(({ group, columns: groupColumns }) => {
          const allOn = groupColumns.every(c => enabled.has(c.key));
          return (
            <section key={group.key} className="ec-group">
              <header className="ec-group-header">
                <h4>{group.label}</h4>
                <button
                  type="button"
                  className="ec-group-toggle"
                  onClick={() => setGroupEnabled(group.key, !allOn)}
                >
                  {allOn ? 'Clear all' : 'Select all'}
                </button>
              </header>

              <div className="ec-grid">
                {groupColumns.map(column => {
                  const isOn = enabled.has(column.key);
                  const isMandatory = mandatory.has(column.key);
                  return (
                    <div key={column.key} className={`ec-item ${isOn ? 'is-on' : ''}`}>
                      <label className="ec-item-main">
                        <input type="checkbox" checked={isOn} onChange={() => toggleEnabled(column.key)} />
                        <span className="ec-item-label">{column.label}</span>
                        {column.writeTarget === null && (
                          <span className="ec-badge ec-badge-warn" title="Read from the sheet but not stored yet">
                            not stored
                          </span>
                        )}
                      </label>

                      <button
                        type="button"
                        className={`ec-req ${isMandatory ? 'is-on' : ''}`}
                        onClick={() => toggleMandatory(column.key)}
                        title="Mark as mandatory"
                      >
                        {isMandatory ? <Check size={12} /> : null} Required
                      </button>

                      {(column.description || column.writeTarget) && (
                        <p className="ec-item-hint" title={column.writeTarget ?? ''}>
                          {column.description ?? column.writeTarget}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </Modal.Body>

      <Modal.Footer className="ec-footer">
        {error && (
          <div className="ec-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader size={16} className="di-spin" /> Saving…
            </>
          ) : (
            'Save Configuration'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ColumnConfigModal;
